import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import { DOW, today } from "../lib/dates.js";

/**
 * A month, to pick a day out of.
 *
 * The date field used to be a list of the twenty-nine days around whatever was
 * already chosen, which is fine for "next Tuesday" and useless for a dentist
 * appointment in March. This can reach any month, and still puts the common
 * case — a day near today — one glance away rather than one scroll.
 *
 * Sunday-first, matching every other grid in the product.
 */

const WEEKS = 6;

export function DatePicker({
  value,
  zone,
  min,
  onPick,
  onClose,
}: {
  /** The currently chosen day, as an ISO date. */
  value: string;
  zone: string;
  /**
   * The earliest day that may be chosen. The end of an event uses it so a
   * trip cannot be made to finish before it starts — a day that cannot be
   * picked says so better than an error that appears after you pick it.
   */
  min?: string;
  onPick: (date: string) => void;
  onClose: () => void;
}): React.JSX.Element {
  const chosen = DateTime.fromISO(value, { zone });
  const [month, setMonth] = useState<DateTime>(() =>
    (chosen.isValid ? chosen : DateTime.now().setZone(zone)).startOf("month"),
  );

  const todayIso = today(zone);

  /*
   * Six weeks, always. A grid that changes height as you page months makes the
   * buttons move under a thumb that is already reaching for one.
   */
  const days = useMemo(() => {
    const first = month.startOf("month");
    const start = first.minus({ days: first.weekday % 7 });
    return Array.from({ length: WEEKS * 7 }, (_, i) => start.plus({ days: i }));
  }, [month]);

  return (
    <div className="repeat" onClick={onClose}>
      <div className="repeat__panel picker" onClick={(e) => e.stopPropagation()}>
        <div className="picker__head">
          <button
            type="button"
            className="picker__page"
            aria-label="Previous month"
            onClick={() => setMonth(month.minus({ months: 1 }))}
          >
            ‹
          </button>
          <span className="picker__month">{month.toFormat("LLLL yyyy")}</span>
          <button
            type="button"
            className="picker__page"
            aria-label="Next month"
            onClick={() => setMonth(month.plus({ months: 1 }))}
          >
            ›
          </button>
        </div>

        <div className="picker__dow">
          {DOW.map((d) => (
            <span key={d}>{d.slice(0, 1)}</span>
          ))}
        </div>

        <div className="picker__grid">
          {days.map((day) => {
            const iso = day.toISODate() ?? "";
            const outside = day.month !== month.month;
            const tooEarly = min !== undefined && iso < min;

            return (
              <button
                key={iso}
                type="button"
                disabled={tooEarly}
                className={[
                  "picker__day",
                  outside ? "is-outside" : "",
                  iso === todayIso ? "is-today" : "",
                  iso === value ? "is-chosen" : "",
                  tooEarly ? "is-unreachable" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-pressed={iso === value}
                aria-label={day.toFormat("cccc, LLLL d, yyyy")}
                onClick={() => onPick(iso)}
              >
                {day.day}
              </button>
            );
          })}
        </div>

        <div className="picker__feet">
          <button
            type="button"
            className="picker__jump"
            onClick={() => setMonth(DateTime.now().setZone(zone).startOf("month"))}
          >
            This month
          </button>
          <button type="button" className="picker__jump" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
